
// Imports

import TWEEN from 'tween.js';
import THREE from './three.js';
import Preloader from './preloader.js';
import MouseControls from './mouse-controls.js';

// Globals

let timerRef = null;
let frameRef = null;
let textures = [];
let currentTexture = 0;
let paused = true;

const header = document.querySelector( 'body > header' );
const stage = document.getElementById( 'stage' );
const stageVideo = document.querySelector( '#stage video' );
const stageCanvas = document.querySelector( '#stage canvas' );
const stageButton = document.querySelector( '#stage button' );
const stageProgress = document.querySelector( '#stage .progress' );
const animationActors = document.querySelectorAll( '[data-animate]' );

const mouseCoords = new THREE.Vector2();
const viewportSize = new THREE.Vector2();
const cameraRotationRadians = Math.PI / 40000;
const cameraRotationQuaternion = new THREE.Quaternion();
cameraRotationQuaternion.setFromAxisAngle( new THREE.Vector3( 0, 1, 0 ), cameraRotationRadians );

const env = {
 
  scene: null,
  clock: null,
  camera: null,
  renderer: null,
  raycaster: null,
  controls: null

}

const obj = {

  title: null,
  imageSphere: null,
  transitionSphere: null

};

// Functions

function setAnimationQueues() {

  for ( let i = 0; i < animationActors.length; i ++ ) {

    let actor = animationActors[ i ];
    let offset = parseInt( actor.getAttribute( 'data-animate' ) || 0 );
    actor.setAttribute( 'data-animate-at', actor.offsetTop - document.body.clientHeight + offset );

  }

  if ( document.body.clientWidth < 768 ) 
    for ( let i = 0; i < animationActors.length; i ++ ) 
      animationActors[ i ].classList.add( 'animate' );

}

function triggerAnimationQueues() {

  const position = document.documentElement.scrollTop || document.body.scrollTop;

  for ( let i = 0; i < animationActors.length; i ++ )
    if ( position >= parseFloat( animationActors[ i ].getAttribute( 'data-animate-at' ) ) )
      animationActors[ i ].classList.add( 'animate' );

}

function handleScrollAnimations() {

  setAnimationQueues();
  window.addEventListener( 'resize', setAnimationQueues );
  window.addEventListener( 'scroll', triggerAnimationQueues ); 

}

function createEnvironment() {

  const cameraNear = 1;
  const cameraFar = 5000;
  const cameraFOV = 75;

  env.camera = new THREE.PerspectiveCamera( cameraFOV, window.innerWidth / window.innerHeight, cameraNear, cameraFar );
  env.camera.position.set( 0, 0, 0 );

  env.controls = new MouseControls( env.camera, stageCanvas );
  env.controls.xMin = -Math.PI / 2 + 0.1;
  env.controls.xMax = Math.PI / 2 - 0.1;
  env.controls.enabled = false;

  env.scene = new THREE.Scene();
  env.renderer = new THREE.WebGLRenderer( { canvas: stageCanvas, antialias: true, alpha: true } );
  env.renderer.setPixelRatio( window.devicePixelRatio );
  env.renderer.gammaOutput = true;
  env.renderer.gammaInput = true;

  env.clock = new THREE.Clock();
  env.raycaster = new THREE.Raycaster();

}

function createLights() {

  const ambientLight = new THREE.AmbientLight( 0xf0f0f0 );
  env.scene.add( ambientLight );

}

function createSpheres() {

  obj.transitionSphere = new THREE.Mesh(
    new THREE.SphereGeometry( 100, 100, 100 ),
    new THREE.MeshBasicMaterial( { map: new THREE.Texture(), transparent: true, opacity: 0 } )
  );

  obj.transitionSphere.position.set( 0, 0, 0 );
  obj.transitionSphere.scale.x = -1;
  env.scene.add( obj.transitionSphere );

  obj.imageSphere = new THREE.Mesh(
    new THREE.SphereGeometry( 200, 200, 100 ),
    new THREE.MeshBasicMaterial( { map: new THREE.Texture() } )
  );

  obj.imageSphere.position.set( 0, 0, 0 );
  obj.imageSphere.scale.x = -1;
  env.scene.add( obj.imageSphere );

}

function handleCanvasResizing() {

  const titleRatio = 0.035 / 800;

  function resize() {

    viewportSize.x = document.body.clientWidth;
    viewportSize.y = document.body.clientHeight;
    env.renderer.setSize( viewportSize.x, viewportSize.y );
    env.camera.aspect = viewportSize.x / viewportSize.y;
    env.camera.updateProjectionMatrix();

    const titleScale = viewportSize.x <= 800 ? titleRatio * viewportSize.x : 0.035;
    obj.title.scale.set( titleScale, titleScale, titleScale );
    obj.title.visible = !( viewportSize.x < 480 || viewportSize.y < 450 );

  }

  window.addEventListener( 'resize', resize, false );
  resize();

}

function handleMouseControls() {

  stageCanvas.addEventListener( 'mouseup', event => env.controls.enabled = false );
  stageCanvas.addEventListener( 'mousedown', event => env.controls.enabled = true );
  stageCanvas.addEventListener( 'mouseleave', event => env.controls.enabled = false );
  stageCanvas.addEventListener( 'mousemove', event => {

    mouseCoords.x =  ( event.clientX / viewportSize.x ) * 2 - 1;
    mouseCoords.y = -( event.clientY / viewportSize.y ) * 2 + 1;

  }, false );

}

function handleStageButton() {

  stageButton.addEventListener( 'click', event => {

    if ( stage.hasAttribute( 'data-video' ) ) {

      stageVideo.pause();
      unlock();
      scrollToPosition( document.body.clientHeight, () => {

        stage.removeAttribute( 'data-video' );
        play();

      } );

    } else {

      pause();
      scrollToPosition( 0, lock );
      header.setAttribute( 'data-color', 'dark' );
      stage.setAttribute( 'data-color', 'dark' );
      stage.setAttribute( 'data-video', '' );
      stageVideo.currentTime = 0;
      stageVideo.play();

    }

  } );

}

function handleVideoEnd() {

  stageVideo.addEventListener( 'ended', event => {

    stageVideo.pause();
    unlock();
    scrollToPosition( document.body.clientHeight, () => {

      stage.removeAttribute( 'data-video' );
      play();

    } );

  } );

}

function transition( texture, direction, distance, duration ) {

  if ( !( direction instanceof THREE.Vector3 ) )
    throw new Error( 'transition direction must be an instance of THREE.Vector3' );

  if ( 0 > distance || distance > 1 )
    throw new Error( 'transition distance must be at or between 0 and 1' );

  if ( 0 > duration )
    throw new Error( 'transition duration must be non-zero and given in milliseconds' );

  obj.transitionSphere.material.map = texture;
  obj.transitionSphere.material.needsUpdate = true;

  setTimeout( () => {

    obj.transitionSphere.position.set( 0, 0, 0 );
    obj.transitionSphere.position.addVectors( env.camera.position, direction.clone().multiplyScalar( 75 * distance ) );

    const positionTween = new TWEEN.Tween( obj.transitionSphere.position )
      .to( env.camera.position, duration )
      .easing( TWEEN.Easing.Quadratic.Out )
      .start();

    const opacityTween = new TWEEN.Tween( { opacity: 0.0 } )
      .to( { opacity: 1.0 }, duration + 100 )
      .onUpdate( function() { obj.transitionSphere.material.opacity = this.opacity; } )
      .onComplete( function() {

        obj.imageSphere.material.map = texture;
        obj.transitionSphere.material.opacity = 0.0;

      } )
      .start();

  }, 100 );

}

function transitionLoop() {

  const colorNames = [ 'light', 'light', 'dark' ];
  header.setAttribute( 'data-color', colorNames[ currentTexture ] );
  stage.setAttribute( 'data-color', colorNames[ currentTexture ] );

  const colors = {
    light: new THREE.Color( 0xffffff ),
    dark: new THREE.Color( 0x000000 )
  };

  const colorTween = new TWEEN.Tween( obj.title.material.color )
    .to( colors[ colorNames[ currentTexture ] ] )
    .onUpdate( function() { obj.title.material.color.setRGB( this.r, this.g, this.b ) } )
    .start();

  transition( textures[ currentTexture ], new THREE.Vector3( 0, 0, 0 ), 0, 800 );
  currentTexture = currentTexture + 1 === textures.length ? 0 : currentTexture + 1;
  timerRef = setTimeout( transitionLoop, 15000 );

}

function render() {

  const delta = env.clock.getDelta();
  const elapsed = env.clock.getElapsedTime();

  env.raycaster.setFromCamera( mouseCoords, env.camera );
  env.camera.quaternion.premultiply( cameraRotationQuaternion );
  env.controls.orientation.y += cameraRotationRadians;
  env.controls.update();

  obj.title.position.addVectors( env.camera.position, env.camera.getWorldDirection().multiplyScalar( 50 ) );
  obj.title.lookAt( env.camera.position );

  const intersects = env.raycaster.intersectObjects( env.scene.children );
  obj.title.userData.distortionActive = false;

  for ( let i = 0; i < intersects.length; i++ ) {

    intersects[i].object.userData.distortionEpicenter = intersects[i].point;
    intersects[i].object.userData.distortionActive = true;

  }

  THREE.MeshDistortionMaterial.update( elapsed );
  if ( !paused ) frameRef = requestAnimationFrame( render );
  env.renderer.render( env.scene, env.camera );
  TWEEN.update();

}

function play() {

  if ( !paused ) return;

  paused = false;
  transitionLoop();
  render();

}

function pause() {

  if ( paused ) return;

  clearTimeout( timerRef );
  cancelAnimationFrame( frameRef );
  currentTexture = 0;
  paused = true;

}

var scrollToPosition = ( function() {

  var ref = null;
  var duration = 400;

  return function( position, callback ) {

    var start = null;
    var initial = document.documentElement.scrollTop || document.body.scrollTop;
    var distance = position - initial;

    function step( now ) {

      if ( !start ) start = now;
      var progress = ( now - start ) / duration;

      document.documentElement.scrollTop = progress * distance + initial;
      document.body.scrollTop = progress * distance + initial;

      if ( progress < 0.999 ) ref = requestAnimationFrame( step );
      else if ( typeof callback ) callback();
 
    }

    cancelAnimationFrame( ref );
    requestAnimationFrame( step );

  };

} )();

function lock() {

  document.body.classList.add( 'locked' );

}

function unlock() {

  document.body.classList.remove( 'locked' );

}

// Environment

lock();
createEnvironment();
createLights();
createSpheres();

// Preloading

Preloader.load( {
  type: 'texture',
  name: 'desert-canyon',
  url:  'textures/desert-canyon.jpg'
} );

Preloader.load( {
  type: 'texture',
  name: 'grass-hills',
  url:  'textures/grass-hills.jpg'
} );

Preloader.load( {
  type: 'texture',
  name: 'snowy-mountains',
  url:  'textures/snowy-mountains.jpg'
} );

Preloader.load( {
  type: 'obj',
  name: 'title',
  obj:  'objects/title.obj'
} );

Preloader.after(
  [ 'desert-canyon', 'grass-hills', 'snowy-mountains' ],
  ( ...textureArray ) => textures.push.apply( textures, textureArray )
);

Preloader.after( 'title', group => {

  const geometry = THREE.DistortionModifier.modify( group.children[ 0 ].geometry );
  const material = new THREE.MeshDistortionMaterial( {
    amplitude: 4.0,
    speed: 6.0,
    frequency: 1.0,
    power: 19.0,
    radius: 670.0,
    sharpness: 0.0,
    durationIn: 0.8,
    durationOut: 0.2
  } );

  material.copy( group.children[ 0 ].material );
  material.transparent = true;
  material.opacity = 1;

  obj.title = new THREE.Mesh( geometry, material );
  env.scene.add( obj.title );

} );

Preloader.after( 'all', () => {

  play();

  setTimeout( () => {

    unlock();
    stage.classList.add( 'ready' );

  }, 250 );

  // Events

  handleCanvasResizing();
  handleMouseControls();
  handleScrollAnimations();
  handleStageButton();
  handleVideoEnd();

} );

Preloader.onUpdate( progress => {

  stageProgress.style.opacity = 1.0 - progress;
  stageProgress.style.width = `${ Math.floor( progress * 100 ) }%`;

} );

// To prevent scrolling on refresh.

window.addEventListener( 'beforeunload', () => {

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

} );




// WEBPACK FOOTER //
// ./source/scripts/index.js